/**
 * Command Executor - Handles command submission and optimistic updates
 * 
 * CRDT Architecture:
 * - Commands queue to IndexedDB syncQueue (persistent, sent to server)
 * - Commands apply optimistically to MEMORY state only (not IndexedDB server truth)
 * - Memory state = IndexedDB (server truth) + pending commands
 * - Commands use same mutation functions as events (shared business logic)
 * - Server responds with event → IndexedDB updated → memory reloaded
 * 
 * Registry Pattern:
 * - Command handlers live in separate file (handlers.ts)
 * - Adding new command: just add handler to registry
 * - TypeScript ensures exhaustive command coverage
 * 
 * Architecture:
 * - Pure domain service (no infrastructure dependencies)
 * - Does NOT broadcast to other tabs (TabSyncCoordinator handles that)
 * - Does NOT queue to server (useCommand hook handles that)
 */


import type { Command, CommandResponse } from './types'
import { logger } from '../utils/logger'
import { memoryStateService } from '../state/memory-store'

class CommandExecutor {
  /**
   * Execute a command - apply optimistically to memory state
   * NOTE: Command should already be queued in IndexedDB by the caller
   * NOTE: Caller (TabSyncCoordinator) is responsible for broadcasting to other tabs
   */
  async executeCommand(command: Command): Promise<CommandResponse> {
    logger.info('[CommandExecutor] 🚀 Executing command:', command.commandType)
    
    try {
      // Apply command optimistically to memory state (using registry pattern)
      logger.info('[CommandExecutor] Applying command to memory...')
      await memoryStateService.applyCommand(command)
      logger.info('[CommandExecutor] ✅ Applied command to memory')
      
      // Return success - coordinator will handle broadcasting
      return {
        status: 'success',
        commandId: command.commandId
      }
    } catch (error: any) {
      logger.error('[CommandExecutor] Error applying command to memory:', error)
      return {
        status: 'error' as const,
        commandId: command.commandId,
        error: error.message || 'Failed to apply command optimistically'
      }
    }
  }
}

export const commandExecutor = new CommandExecutor()
